// 庫存同步 -- the page that replaces four manual steps with one button.
//
// TWO BUTTONS, NOT ONE. Today the staff download a diff spreadsheet and SEE it
// before uploading it, and going straight to one-click-apply would delete that
// check without anybody deciding to. 試算 runs the identical code path with the
// writes turned off, so what it shows is what 同步 would do.
//
// It polls a run document rather than holding a request open: a pass over a few
// thousand skus takes far longer than an HTTP request should live, and polling
// also means a pass somebody else started is visible here.

import { useState, useEffect, useCallback } from 'react'
import { object, bool, number, string, arrayOf, shape } from 'prop-types'

import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import LinearProgress from '@mui/material/LinearProgress'

import { REASONS, OUTCOMES, STATUSES, MODES } from '../../vendorSync/reasons.js'
// Next door, in plain javascript: it was split out when the runner could not
// load a .jsx at all, which made everything in this file untestable.
import { inFlight, stranded, when, labelOf } from './runState.js'

// Two endpoints, not one with a mode: route authority is per name and method,
// so 同步 needs its own name to be grantable separately from 試算.
const URL = '/api/route/stockSync'
const APPLY_URL = '/api/route/stockSyncApply'
const POLL_MS = 3000

// The order a pass reads in: what it looked at, what it meant to send, then
// what became of it. `carried` is last because it is about the PREVIOUS pass --
// rows it could not reach, picked up by this one.
const COUNTS = [
  ['considered', '檢查'],
  ['write', '需更新'],
  ['skip', '略過'],
  ['ok', '已更新'],
  ['refused', '平台拒絕'],
  ['failed', '稍後重試'],
  ['carried', '接續重試'],
]

async function call(url, method, params) {
  const query = Object.keys(params)
    .map(key => `${key}=${encodeURIComponent(params[key])}`)
    .join('&')

  const response = await fetch(`${url}?${query}`, {
    method,
    credentials: 'same-origin',
  })
  const body = await response.json()

  if (!response.ok)
    throw new Error(
      body && body.message ? body.message : `HTTP ${response.status}`,
    )

  return body
}

function Counts({ counts }) {
  if (!counts) return null

  return (
    <Table size="small" style={{ maxWidth: 640 }}>
      <TableHead>
        <TableRow>
          {COUNTS.map(([key, label]) => (
            <TableCell key={key} align="right">
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        <TableRow>
          {COUNTS.map(([key]) => (
            <TableCell key={key} align="right">
              {counts[key] ? counts[key] : 0}
            </TableCell>
          ))}
        </TableRow>
      </TableBody>
    </Table>
  )
}

Counts.propTypes = { counts: object }

function Reasons({ reasons }) {
  const keys = reasons ? Object.keys(reasons) : []
  if (keys.length === 0) return null

  return (
    <div style={{ marginTop: 16 }}>
      {keys.map(key => (
        <span key={key} style={{ marginRight: 16 }}>
          {labelOf(REASONS, key)}: <strong>{reasons[key]}</strong>
        </span>
      ))}
    </div>
  )
}

Reasons.propTypes = { reasons: object }

// Only the rows that need a human. A clean pass shows nothing here, which is
// the point -- an empty table means there is nothing to do.
//
// THE ROWS COME FROM stockSyncItems, not from the run document. They used to be
// a capped copy embedded on the run, which meant the page re-read up to five
// hundred of them every three seconds and saw none of them until the pass
// settled. These arrive as the pass writes them.
function Results({ items, more }) {
  const rows = items || []
  if (rows.length === 0) return null

  return (
    <div style={{ marginTop: 24, overflowX: 'auto' }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>貨號</TableCell>
            <TableCell align="right">數量</TableCell>
            <TableCell>結果</TableCell>
            <TableCell>原因</TableCell>
            <TableCell>說明</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={`${row.sku}-${i}`}>
              <TableCell>{row.sku}</TableCell>
              <TableCell align="right">
                {row.quantity === null ? '-' : row.quantity}
              </TableCell>
              <TableCell>{labelOf(OUTCOMES, row.outcome)}</TableCell>
              <TableCell>
                {row.reason ? labelOf(REASONS, row.reason) : ''}
              </TableCell>
              <TableCell>
                {row.problems && row.problems.length
                  ? row.problems.join('、')
                  : ''}
                {row.message ? ` ${row.message}` : ''}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {more && (
        <div style={{ marginTop: 8 }}>結果過多，僅顯示前 {rows.length} 筆</div>
      )}
    </div>
  )
}

Results.propTypes = {
  items: arrayOf(
    shape({
      sku: string,
      quantity: number,
      outcome: string,
      reason: string,
      message: string,
      // Rendered beside the message, and the reason a row that LANDED can still
      // be on this table -- so it belongs in the shape the table declares.
      problems: arrayOf(string),
    }),
  ),
  more: bool,
}

function StockSync() {
  const [run, setRun] = useState(null)
  // The rows needing attention, read from stockSyncItems rather than carried on
  // the run document -- so they arrive as the pass writes them.
  const [items, setItems] = useState([])
  const [more, setMore] = useState(false)
  const [error, setError] = useState(null)
  const [starting, setStarting] = useState(false)
  const [confirming, setConfirming] = useState(false)

  // useCallback with no deps because every one of these is a useState setter,
  // which react keeps stable -- so `show` never needs to change, and `refresh`
  // below can depend on it honestly rather than closing over a fresh copy each
  // render and claiming an empty dependency list.
  const show = useCallback(body => {
    setRun(body.run)
    setItems(body.items || [])
    setMore(Boolean(body.more))
  }, [])

  const refresh = useCallback(async () => {
    try {
      const body = await call(URL, 'GET', {})
      show(body)
      setError(null)
    } catch (err) {
      setError(err.message)
    }
  }, [show])

  const running = inFlight(run)

  useEffect(() => {
    refresh()
  }, [refresh])

  // Poll only while something is in flight. An idle page asking every three
  // seconds forever is a cost nobody chose. A lapsed lease ends the polling by
  // itself: each answer re-renders, and inFlight goes false once the clock
  // passes expiresAt even though the document never changed.
  useEffect(() => {
    if (!running) return undefined

    const timer = setInterval(refresh, POLL_MS)

    return () => clearInterval(timer)
  }, [running, refresh])

  const start = async mode => {
    setStarting(true)
    setConfirming(false)
    try {
      const body = await call(mode === 'apply' ? APPLY_URL : URL, 'POST', {})
      show(body)
      setError(body.status === 'busy' ? STATUSES.busy : null)
    } catch (err) {
      setError(err.message)
    }
    setStarting(false)
  }

  const disabled = running || starting
  const previewed = Boolean(
    run && run.mode === 'preview' && run.status === 'done',
  )

  return (
    <div style={{ padding: 24 }}>
      <h1>庫存同步</h1>

      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Button
          variant="outlined"
          color="primary"
          disabled={disabled}
          onClick={() => start('preview')}
        >
          試算
        </Button>
        <Button
          variant="contained"
          color="secondary"
          disabled={disabled}
          onClick={() => setConfirming(true)}
          style={{ marginLeft: 12 }}
        >
          同步
        </Button>
        <Button
          onClick={refresh}
          disabled={starting}
          style={{ marginLeft: 12 }}
        >
          重新整理
        </Button>
      </div>

      {running && <LinearProgress style={{ marginTop: 16 }} />}
      {error && <div style={{ marginTop: 16, color: '#c62828' }}>{error}</div>}

      {run && (
        <div style={{ marginTop: 24 }}>
          <div>
            <span style={{ marginRight: 16 }}>
              {labelOf(MODES, run.mode)} — {labelOf(STATUSES, run.status)}
            </span>
            <span style={{ marginRight: 16 }}>開始 {when(run.startedAt)}</span>
            {run.finishedAt ? <span>結束 {when(run.finishedAt)}</span> : null}
          </div>
          {stranded(run) && (
            <div style={{ marginTop: 8, color: '#ef6c00' }}>
              這次同步沒有正常結束（可能是程式重新啟動），已超過執行期限{' '}
              {when(run.expiresAt)}， 可以重新開始。
            </div>
          )}
          {run.error && (
            <div style={{ marginTop: 8, color: '#c62828' }}>{run.error}</div>
          )}
          <div style={{ marginTop: 16 }}>
            <Counts counts={run.counts} />
          </div>
          <Reasons reasons={run.reasons} />
          <Results items={items} more={more} />
        </div>
      )}

      <Dialog open={confirming} onClose={() => setConfirming(false)}>
        <DialogTitle>確認同步</DialogTitle>
        <DialogContent>
          <DialogContentText>
            將把我們的庫存數量寫入 SHOPLINE，直接覆蓋平台上的數量。
            {previewed
              ? ` 上次試算需更新 ${run.counts && run.counts.write ? run.counts.write : 0} 筆。`
              : ' 建議先執行試算。'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>取消</Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => start('apply')}
          >
            確定同步
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

// The pieces a run document is rendered through. Exported because rendering
// the page itself only ever shows the empty state -- it reads its run from a
// fetch, and a static render runs no effects -- so these are the only way to
// put a document of the wrong shape in front of the markup that has to survive
// it. See ./__tests__/StockSync.spec.jsx.
export { Counts, Reasons, Results }

export default StockSync
